import React, { useState } from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import BlogLayout from '@/components/admin/BlogLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Save, Send } from 'lucide-react';
import { TipTapEditor } from '@/components/editor/TipTapEditor';

interface Author {
  id: number;
  name: string;
  email: string;
}

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface Tag {
  id: number;
  name: string;
  slug: string;
}

interface Post {
  id?: number;
  title: string;
  slug: string;
  summary: string;
  content: string;
  ai_markdown: string;
  tiptap_json: Record<string, unknown>;
  content_html: string;
  cover_image: string;
  status: 'draft' | 'published' | 'archived';
  visibility: 'visible' | 'auth_only';
  published_at: string | null;
  author: Author;
  category_ids: number[];
  tag_ids: number[];
}

interface PostFormProps {
  post?: Post;
  categories: Category[];
  tags: Tag[];
  isEditing?: boolean;
}

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const visibilityOptions = [
  { value: 'visible', label: 'Public', description: 'Anyone can view this post' },
  { value: 'auth_only', label: 'Members Only', description: 'Logged-in users only' },
];

const PostForm: React.FC<PostFormProps> = ({ post, categories, tags, isEditing = false }) => {
  const { errors } = usePage().props as any;
  
  const [formData, setFormData] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    summary: post?.summary || '',
    content: post?.content || '',
    ai_markdown: post?.ai_markdown || '',
    tiptap_json: post?.tiptap_json || {},
    content_html: post?.content_html || '',
    cover_image: post?.cover_image || '',
    status: post?.status || 'draft',
    visibility: post?.visibility || 'visible',
    published_at: post?.published_at || '',
    category_ids: post?.category_ids || [],
    tag_ids: post?.tag_ids || [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditorChange = (html: string, json: Record<string, unknown>) => {
    setFormData((prev) => ({
      ...prev,
      content: html,
      tiptap_json: json,
      content_html: html, // Store HTML for server-side rendering
    }));
  };

  const handleAiMarkdownChange = (markdown: string) => {
    setFormData((prev) => ({
      ...prev,
      ai_markdown: markdown,
    }));
  };

  const handleCategoryChange = (categoryId: number, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      category_ids: checked
        ? [...prev.category_ids, categoryId]
        : prev.category_ids.filter((id) => id !== categoryId),
    }));
  };

  const handleTagChange = (tagId: number, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      tag_ids: checked
        ? [...prev.tag_ids, tagId]
        : prev.tag_ids.filter((id) => id !== tagId),
    }));
  };

  const handleSubmit = (publish: boolean = false) => {
    setIsSubmitting(true);
    
    const data = {
      title: formData.title,
      slug: formData.slug,
      summary: formData.summary,
      content: formData.content,
      ai_markdown: formData.ai_markdown,
      tiptap_json: formData.tiptap_json,
      content_html: formData.content_html,
      cover_image: formData.cover_image,
      status: publish ? 'published' : formData.status,
      published_at: publish ? new Date().toISOString() : formData.published_at,
      visibility: formData.visibility,
      category_ids: formData.category_ids,
      tag_ids: formData.tag_ids,
    };

    const url = isEditing && post?.id 
      ? `/admin/blog/posts/${post.id}` 
      : '/admin/blog/posts';
    
    const method = isEditing && post?.id ? 'put' : 'post';

    router.visit(url, {
      method: method as 'post' | 'put' | 'patch',
      data: { blog_post: data },
      onFinish: () => setIsSubmitting(false),
    });
  };

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Blog', href: '/admin/blog/posts' },
    { label: isEditing ? 'Edit Post' : 'New Post' },
  ];

  return (
    <BlogLayout breadcrumbs={breadcrumbs}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link href="/admin/blog/posts">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {isEditing ? 'Edit Post' : 'New Post'}
              </h1>
              <p className="text-muted-foreground">
                {isEditing ? 'Update your blog post' : 'Create a new blog post'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleSubmit(false)}
              disabled={isSubmitting}
            >
              <Save className="mr-2 h-4 w-4" />
              Save Draft
            </Button>
            {formData.status !== 'published' && (
              <Button
                onClick={() => handleSubmit(true)}
                disabled={isSubmitting}
              >
                <Send className="mr-2 h-4 w-4" />
                Publish
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Content</CardTitle>
                <CardDescription>
                  The main content of your blog post
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="title">Title</Label>
                    <span className="text-xs text-muted-foreground">
                      {formData.title.length}/60 (recommended)
                    </span>
                  </div>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Enter post title"
                  />
                  {errors?.title && (
                    <p className="text-sm text-red-500">{errors.title}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="slug">Slug</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => handleChange('slug', e.target.value)}
                    placeholder="post-url-slug"
                  />
                  {errors?.slug && (
                    <p className="text-sm text-red-500">{errors.slug}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="summary">Summary</Label>
                    <span className="text-xs text-muted-foreground">
                      {formData.summary.length}/180 (recommended)
                    </span>
                  </div>
                  <Textarea
                    id="summary"
                    value={formData.summary}
                    onChange={(e) => handleChange('summary', e.target.value)}
                    placeholder="Brief summary of the post"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content">Content</Label>
                  
                  <TipTapEditor
                    content={formData.content}
                    tiptapJson={formData.tiptap_json}
                    onChange={handleEditorChange}
                    placeholder="Start writing your blog post..."
                    enableAiMarkdown={true}
                    aiMarkdown={formData.ai_markdown}
                    onAiMarkdownChange={handleAiMarkdownChange}
                    showConvertButton={true}
                  />
                  {errors?.content && (
                    <p className="text-sm text-red-500">{errors.content}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Publishing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleChange('status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="visibility">Visibility</Label>
                  <Select
                    value={formData.visibility}
                    onValueChange={(value) => handleChange('visibility', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select visibility" />
                    </SelectTrigger>
                    <SelectContent>
                      {visibilityOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div>
                            <div>{option.label}</div>
                            <div className="text-xs text-muted-foreground">
                              {option.description}
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="published_at">Published At</Label>
                  <Input
                    id="published_at"
                    type="datetime-local"
                    value={formData.published_at}
                    onChange={(e) => handleChange('published_at', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cover_image">Cover Image</Label>
                  <Input
                    id="cover_image"
                    value={formData.cover_image}
                    onChange={(e) => handleChange('cover_image', e.target.value)}
                    placeholder="/images/mind-connect.webp"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave empty to auto-extract from content
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Categories</CardTitle>
                <CardDescription>Select categories for this post</CardDescription>
              </CardHeader>
              <CardContent>
                {categories.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No categories available</p>
                ) : (
                  <div className="space-y-2">
                      {categories.map((category) => (
                        <div key={category.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`category-${category.id}`}
                            checked={formData.category_ids.includes(category.id)}
                            onCheckedChange={(checked: boolean | string) =>
                              handleCategoryChange(category.id, !!checked)
                            }
                          />
                          <Label htmlFor={`category-${category.id}`} className="text-sm cursor-pointer">
                            {category.name}
                          </Label>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Tags</CardTitle>
                <CardDescription>Select tags for this post</CardDescription>
              </CardHeader>
              <CardContent>
                {tags.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No tags available</p>
                ) : (
                  <div className="space-y-2">
                    {tags.map((tag) => (
                      <div key={tag.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`tag-${tag.id}`}
                          checked={formData.tag_ids.includes(tag.id)}
                          onCheckedChange={(checked: boolean | string) =>
                            handleTagChange(tag.id, !!checked)
                          }
                        />
                        <Label htmlFor={`tag-${tag.id}`} className="text-sm cursor-pointer">
                          {tag.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </BlogLayout>
  );
};

export default PostForm;
